import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowsClockwiseIcon,
  BellRingingIcon,
  CaretRightIcon,
  InfoIcon,
  PlusCircleIcon,
  RankingIcon,
  TrashIcon,
  UserPlusIcon,
  WarningIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardEyebrow,
  CardHeader,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import {
  addCopyFollow,
  listCopyFollows,
  listCopyLeaders,
  removeCopyFollow,
  type CopyFollow,
  type CopyLeader,
} from '../api';
import { useAuth } from '../auth';
import { useT, type TFunction } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';

const ADDR_RE = /^0x[0-9a-fA-F]{40}$/;

function short(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function Copy() {
  const t = useT();
  const auth = useAuth();
  useTelegramBackButton();
  useNavStackEntry({ label: t('copy.breadcrumb') });

  const [follows, setFollows] = useState<CopyFollow[] | null>(null);
  const [leaders, setLeaders] = useState<CopyLeader[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addAddr, setAddAddr] = useState('');
  const [addMax, setAddMax] = useState('');
  const [addMult, setAddMult] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [removingAddr, setRemovingAddr] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const ready = auth.state === 'ready';

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listCopyFollows();
      setFollows(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadLeaders = useCallback(async () => {
    try {
      const data = await listCopyLeaders('30d', 30);
      setLeaders(data);
    } catch {
      setLeaders([]);
    }
  }, []);

  useEffect(() => {
    if (!ready) return;
    void reload();
    void reloadLeaders();
  }, [ready, reload, reloadLeaders]);

  const onSubmitAdd = useCallback(
    async (ev: React.FormEvent<HTMLFormElement>) => {
      ev.preventDefault();
      setAddError(null);
      const addr = addAddr.trim();
      if (!ADDR_RE.test(addr)) {
        setAddError(t('copy.invalid_address'));
        return;
      }
      setAdding(true);
      try {
        const maxN = addMax.trim() === '' ? undefined : Number(addMax);
        const multN = addMult.trim() === '' ? undefined : Number(addMult);
        await addCopyFollow({
          leaderAddress: addr,
          ...(maxN !== undefined && Number.isFinite(maxN)
            ? { maxUsdPerTrade: maxN }
            : {}),
          ...(multN !== undefined && Number.isFinite(multN)
            ? { sizeMultiplier: multN }
            : {}),
        });
        setAddAddr('');
        setAddMax('');
        setAddMult('');
        await reload();
      } catch (err) {
        setAddError((err as Error).message);
      } finally {
        setAdding(false);
      }
    },
    [addAddr, addMax, addMult, reload, t],
  );

  const onRemove = useCallback(
    async (addr: string) => {
      if (!window.confirm(t('copy.unfollow_confirm'))) return;
      setRemovingAddr(addr);
      setRemoveError(null);
      try {
        await removeCopyFollow(addr);
        await reload();
      } catch (err) {
        setRemoveError((err as Error).message);
      } finally {
        setRemovingAddr(null);
      }
    },
    [reload, t],
  );

  const eyebrow = useMemo(
    () => (
      <span className="inline-flex items-center gap-1.5">
        <UserPlusIcon weight="fill" className="size-3" />
        {t('copy.eyebrow')}
      </span>
    ),
    [t],
  );

  if (auth.state === 'loading' || (loading && follows === null)) {
    return (
      <AuthLoadingScreen
        title={t('copy.loading_title')}
        description={t('copy.loading_desc')}
        shortDescription={t('copy.loading_short')}
        steps={[
          { label: 'Session', color: 'bg-emerald-500' },
          { label: t('copy.eyebrow'), color: 'bg-primary', delay: '0.4s' },
        ]}
      />
    );
  }

  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow={eyebrow} title={t('copy.title')}>
        <NavBar />
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('common.auth_error', { error: '' })}</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  const list = follows ?? [];

  return (
    <ScreenShell
      eyebrow={eyebrow}
      title={t('copy.title')}
      description={t('copy.description')}
    >
      <NavBar />

      <Alert tone="info">
        <BellRingingIcon weight="fill" className="size-4" />
        <AlertTitle>{t('copy.notice_title')}</AlertTitle>
        <AlertDescription>{t('copy.notice_desc')}</AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardEyebrow>
            <RankingIcon weight="fill" className="size-3" />
            {t('copy.top_title')}
          </CardEyebrow>
        </CardHeader>
        <CardContent>
          {leaders === null ? (
            <p className="text-muted-foreground text-sm">{t('copy.top_loading')}</p>
          ) : leaders.length === 0 ? (
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t('copy.top_empty')}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {leaders.map((l) => (
                <LeaderRow key={l.address} leader={l} t={t} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('common.auth_error', { error: '' })}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardEyebrow>
            <PlusCircleIcon weight="fill" className="size-3" />
            {t('copy.add_title')}
          </CardEyebrow>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmitAdd} className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
                {t('copy.add_address_label')}
              </span>
              <Input
                value={addAddr}
                onChange={(e) => setAddAddr(e.target.value)}
                placeholder={t('copy.add_address_placeholder')}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                className="font-mono"
                required
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
                  {t('copy.add_max_label')}
                </span>
                <Input
                  value={addMax}
                  onChange={(e) => setAddMax(e.target.value)}
                  type="number"
                  min="1"
                  step="1"
                  inputMode="decimal"
                  placeholder="50"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
                  {t('copy.add_mult_label')}
                </span>
                <Input
                  value={addMult}
                  onChange={(e) => setAddMult(e.target.value)}
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  placeholder="1"
                />
              </label>
            </div>
            {addError && (
              <Alert tone="danger">
                <WarningIcon weight="fill" className="size-4" />
                <AlertTitle>{t('copy.add_error_title')}</AlertTitle>
                <AlertDescription>{addError}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" disabled={adding}>
              <PlusCircleIcon weight="bold" className="size-4" />
              {adding ? t('copy.adding') : t('copy.add_btn')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {removeError && (
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('copy.unfollow_error_title')}</AlertTitle>
          <AlertDescription>{removeError}</AlertDescription>
        </Alert>
      )}

      {list.length === 0 ? (
        <Card>
          <CardHeader>
            <CardEyebrow>
              <InfoIcon weight="fill" className="size-3" />
              {t('copy.empty_title')}
            </CardEyebrow>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t('copy.empty_desc')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2.5">
          {list.map((f) => (
            <FollowRow
              key={f.leaderAddress}
              follow={f}
              t={t}
              removing={removingAddr === f.leaderAddress}
              onRemove={onRemove}
            />
          ))}
        </div>
      )}
    </ScreenShell>
  );
}

function FollowRow({
  follow,
  t,
  removing,
  onRemove,
}: {
  follow: CopyFollow;
  t: TFunction;
  removing: boolean;
  onRemove: (addr: string) => void;
}) {
  const label =
    follow.leader?.pseudonym || follow.leader?.name || short(follow.leaderAddress);
  const avatar = follow.leader?.profileImage ?? null;
  return (
    <div className="bg-card/90 ring-foreground/10 supports-backdrop-filter:bg-card/85 flex flex-col gap-2 rounded-2xl p-3 ring-1 backdrop-blur-md">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/copy/leader/${follow.leaderAddress}`}
          className="group flex min-w-0 flex-1 items-center gap-2.5"
        >
          {avatar ? (
            <img
              src={avatar}
              alt=""
              className="size-9 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span className="bg-primary/15 text-primary flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold">
              {label.slice(0, 2).toUpperCase()}
            </span>
          )}
          <div className="flex min-w-0 flex-col">
            <span className="text-foreground group-hover:text-primary truncate text-[13px] font-semibold transition-colors">
              {label}
            </span>
            <span className="text-muted-foreground font-mono text-[11px] tabular-nums">
              {short(follow.leaderAddress)}
            </span>
          </div>
          <CaretRightIcon
            weight="bold"
            className="text-muted-foreground/60 size-4 shrink-0"
          />
        </Link>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onRemove(follow.leaderAddress)}
          disabled={removing}
        >
          {removing ? (
            <ArrowsClockwiseIcon weight="bold" className="size-3 animate-spin" />
          ) : (
            <TrashIcon weight="bold" className="size-3" />
          )}
          {removing ? t('copy.unfollowing') : t('copy.unfollow_btn')}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone="primary">
          {t('copy.row_max', { max: String(follow.maxUsdPerTrade) })}
        </Badge>
        <Badge tone="info">
          {t('copy.row_mult', { mult: String(follow.sizeMultiplier) })}
        </Badge>
        <Badge tone={follow.active ? 'success' : 'neutral'} className="text-[10px]">
          <span
            className={cn(
              'auth-loading-dot',
              follow.active ? 'bg-emerald-500' : 'bg-foreground/30',
            )}
          />
          {follow.active ? t('copy.active') : t('copy.inactive')}
        </Badge>
      </div>
      <span className="text-muted-foreground text-[11px]">
        {t('copy.row_since', { date: fmtDate(follow.createdAt) })}
      </span>
    </div>
  );
}

function fmtUsdCompact(n: number): string {
  if (!Number.isFinite(n)) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function LeaderRow({ leader, t }: { leader: CopyLeader; t: TFunction }) {
  const label =
    leader.pseudonym || leader.name || short(leader.address);
  return (
    <Link
      to={`/copy/leader/${leader.address}`}
      className="bg-card/90 ring-foreground/10 supports-backdrop-filter:bg-card/85 hover:ring-primary/40 flex items-center gap-3 rounded-2xl p-3 ring-1 backdrop-blur-md transition-colors"
    >
      <span className="text-muted-foreground w-6 shrink-0 text-center text-sm font-bold tabular-nums">
        {leader.rank ?? '—'}
      </span>
      {leader.profileImage ? (
        <img
          src={leader.profileImage}
          alt=""
          className="size-8 shrink-0 rounded-full object-cover"
        />
      ) : (
        <span className="bg-primary/15 text-primary flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold">
          {label.slice(0, 2).toUpperCase()}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-foreground truncate text-[13px] font-semibold">
          {label}
        </span>
        <span className="text-muted-foreground text-[11px]">
          {t('copy.top_volume', { vol: fmtUsdCompact(leader.volumeUsd) })}
          {leader.pnlUsd != null
            ? ` · ${t('copy.top_pnl', { pnl: fmtUsdCompact(leader.pnlUsd) })}`
            : ''}
        </span>
      </div>
      <CaretRightIcon weight="bold" className="text-muted-foreground size-4 shrink-0" />
    </Link>
  );
}
